import { observable, configure, action, runInAction } from 'mobx';
import LocalModel from './localModel';

import util from 'preact-util';

configure({ enforceActions: 'always' });

class HolidayDayStore extends LocalModel {
    constructor() {
        super('holidayDay', {
            namePlural: 'holidayDays',
            namePluralReal: 'holidayDays',
            sort: 'date',
            limit: 10000,
            api: {
                search: {
                    url: '/api/holidaydays/',
                    params: {
                        limit: 10000,
                        sort: 'date',
                    },
                },
                load: {
                    url: '/api/holidaydays/',
                    params: {},
                },
                save: {
                    url: '/api/holidaydays/',
                    params: {},
                },
                delete: {
                    url: '/api/holidaydays/',
                    params: {},
                },
            },
        });
    }

    @observable newHolidayDay = {};

    @observable holidayDay = {};

    @observable holidayDays = [];

    @observable users = [];

    @observable selectedUsers = [];

    @observable year = new Date().getFullYear();

    @observable isLoading = false;

    /**
     * Load holiday days for a year and optional user list
     * @param {number} year - The year to load
     * @param {number[]} userIds - Optional list of user IDs to filter by
     */
    async loadForYear(year, userIds = []) {
        runInAction(() => {
            this.isLoading = true;
        });
        try {
            const params = { year, limit: 10000 };
            if (userIds && userIds.length > 0) {
                params.user = userIds;
            }
            const response = await util.fetchApi('/api/holidaydays/', { method: 'GET' }, params);
            runInAction(() => {
                if (response && response.status === 200) {
                    this.holidayDays = response.data || [];
                    if (response.included?.users) {
                        this.users = response.included.users;
                    }
                }
                this.isLoading = false;
            });
        } catch (err) {
            console.error('Failed to load holiday days:', err);
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    /**
     * Load holiday days for a date range
     * @param {Date|string} startDate
     * @param {Date|string} endDate
     * @param {number[]} userIds - Optional list of user IDs to filter by
     */
    async loadForRange(startDate, endDate, userIds = []) {
        runInAction(() => {
            this.isLoading = true;
        });
        try {
            const params = {
                startDate: typeof startDate === 'string' ? startDate : startDate.toISOString().split('T')[0],
                endDate: typeof endDate === 'string' ? endDate : endDate.toISOString().split('T')[0],
                limit: 10000,
            };
            if (userIds && userIds.length > 0) {
                params.user = userIds;
            }
            const response = await util.fetchApi('/api/holidaydays/', { method: 'GET' }, params);
            runInAction(() => {
                if (response && response.status === 200) {
                    this.holidayDays = response.data || [];
                    if (response.included?.users) {
                        this.users = response.included.users;
                    }
                }
                this.isLoading = false;
            });
        } catch (err) {
            console.error('Failed to load holiday days:', err);
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    /**
     * Toggle a holiday day on/off for a user on a specific date
     * @param {number} userId
     * @param {Date|string} date
     * @param {number} customerId - The customer ID context (for holiday planner)
     * @returns {Promise<{action: 'created'|'removed', holidayDay: object|null}>}
     */
    async toggle(userId, date, customerId = null) {
        try {
            const dateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0];
            const params = {
                user: userId,
                date: dateStr,
            };
            if (customerId) {
                params.customer = customerId;
            }
            const response = await util.fetchApi('/api/holidaydays/toggle', { method: 'POST' }, params);
            if (response && (response.status === 200 || response.status === 201)) {
                const { action: resultAction, holidayDay } = response.data || {};
                runInAction(() => {
                    if (resultAction === 'created' && holidayDay) {
                        // Add to local list
                        this.holidayDays = [...this.holidayDays, holidayDay];
                    } else if (resultAction === 'removed') {
                        // Remove from local list by user and date
                        const targetDate = new Date(dateStr);
                        targetDate.setHours(0, 0, 0, 0);
                        this.holidayDays = this.holidayDays.filter(h => {
                            const hDate = new Date(h.date);
                            hDate.setHours(0, 0, 0, 0);
                            return !(h.user === userId && hDate.getTime() === targetDate.getTime());
                        });
                    }
                });
                return response.data;
            }
        } catch (err) {
            console.error('Failed to toggle holiday day:', err);
        }
        return null;
    }

    /**
     * Check if a user has a holiday on a specific date
     * @param {number} userId
     * @param {Date|string} date
     * @param {string} status - Optional status filter ('approved', 'pending', 'rejected')
     * @returns {boolean}
     */
    hasHoliday(userId, date, status = null) {
        const targetDate = typeof date === 'string' ? new Date(date) : new Date(date);
        targetDate.setHours(0, 0, 0, 0);

        return this.holidayDays.some(h => {
            const hDate = new Date(h.date);
            hDate.setHours(0, 0, 0, 0);
            const dateMatches = h.user === userId && hDate.getTime() === targetDate.getTime();
            if (!dateMatches) return false;
            if (status) return h.status === status;
            return true;
        });
    }

    /**
     * Get the status of a holiday for a user on a specific date
     * @param {number} userId
     * @param {Date|string} date
     * @returns {string|null} - 'approved', 'pending', 'rejected', or null if no holiday
     */
    getHolidayStatus(userId, date) {
        const holiday = this.getHoliday(userId, date);
        if (!holiday) return null;
        // Default to 'approved' for legacy records without status
        return holiday.status || 'approved';
    }

    /**
     * Approve a holiday request
     * @param {number} holidayDayId
     * @returns {Promise<object|null>}
     */
    async approveRequest(holidayDayId) {
        try {
            const response = await util.fetchApi(`/api/holidaydays/${holidayDayId}`, { method: 'PATCH' }, {
                status: 'approved',
            });
            if (response && response.status === 202) {
                const updatedHoliday = response.data?.holidayDay;
                runInAction(() => {
                    // Update the holiday in the local list
                    this.holidayDays = this.holidayDays.map(h =>
                        h.id === holidayDayId ? { ...h, status: 'approved' } : h
                    );
                });
                return updatedHoliday;
            }
        } catch (err) {
            console.error('Failed to approve holiday request:', err);
        }
        return null;
    }

    /**
     * Reject a holiday request
     * @param {number} holidayDayId
     * @returns {Promise<object|null>}
     */
    async rejectRequest(holidayDayId) {
        try {
            const response = await util.fetchApi(`/api/holidaydays/${holidayDayId}`, { method: 'PATCH' }, {
                status: 'rejected',
            });
            if (response && response.status === 202) {
                const updatedHoliday = response.data?.holidayDay;
                runInAction(() => {
                    // Update the holiday in the local list
                    this.holidayDays = this.holidayDays.map(h =>
                        h.id === holidayDayId ? { ...h, status: 'rejected' } : h
                    );
                });
                return updatedHoliday;
            }
        } catch (err) {
            console.error('Failed to reject holiday request:', err);
        }
        return null;
    }

    /**
     * Approve all pending holiday requests for a user (single API call)
     * @param {number} userId
     * @returns {Promise<number>} Number of approved requests
     */
    async approveAllForUser(userId) {
        const pendingHolidays = this.holidayDays.filter(h => h.user === userId && h.status === 'pending');
        if (pendingHolidays.length === 0) {
            return 0;
        }

        const ids = pendingHolidays.map(h => h.id);

        try {
            const response = await util.fetchApi('/api/holidaydays/batch-status', { method: 'PATCH' }, {
                ids,
                status: 'approved',
            });

            if (response && response.status === 200) {
                const updatedIds = response.data?.updatedIds || [];
                runInAction(() => {
                    // Update all approved holidays in the local list
                    this.holidayDays = this.holidayDays.map(h =>
                        updatedIds.includes(h.id) ? { ...h, status: 'approved' } : h
                    );
                });
                return updatedIds.length;
            }
        } catch (err) {
            console.error('Failed to batch approve holiday requests:', err);
        }
        return 0;
    }

    /**
     * Get pending holidays for a user
     * @param {number} userId
     * @returns {object[]}
     */
    getPendingHolidaysForUser(userId) {
        return this.holidayDays.filter(h => h.user === userId && h.status === 'pending');
    }

    /**
     * Get holiday for a user on a specific date
     * @param {number} userId
     * @param {Date|string} date
     * @returns {object|null}
     */
    getHoliday(userId, date) {
        const targetDate = typeof date === 'string' ? new Date(date) : new Date(date);
        targetDate.setHours(0, 0, 0, 0);

        return this.holidayDays.find(h => {
            const hDate = new Date(h.date);
            hDate.setHours(0, 0, 0, 0);
            return h.user === userId && hDate.getTime() === targetDate.getTime();
        }) || null;
    }

    /**
     * Get all holiday days for a specific user
     * @param {number} userId
     * @returns {object[]}
     */
    getHolidaysForUser(userId) {
        return this.holidayDays.filter(h => h.user === userId);
    }

    /**
     * Count holiday days for a user
     * @param {number} userId
     * @param {string} status - Optional status filter ('approved', 'pending', 'rejected')
     * @returns {number}
     */
    countHolidaysForUser(userId, status = null) {
        return this.holidayDays.filter(h => {
            if (h.user !== userId) return false;
            if (status) return h.status === status;
            return true;
        }).length;
    }

    /**
     * Count approved holiday days for a user
     * @param {number} userId
     * @returns {number}
     */
    countApprovedHolidaysForUser(userId) {
        return this.holidayDays.filter(h => {
            if (h.user !== userId) return false;
            // Default to approved for legacy records without status
            return !h.status || h.status === 'approved';
        }).length;
    }

    /**
     * Set the year for the planner view
     * @param {number} year
     */
    @action
    setYear(year) {
        this.year = year;
    }

    /**
     * Load holiday users from the API
     */
    async loadHolidayUsers() {
        try {
            const response = await util.fetchApi('/api/holidayusers/', { method: 'GET' }, { limit: 1000 });
            if (response && response.status === 200) {
                const holidayUsers = response.data || [];
                const users = response.included?.users || [];

                // Map holiday users to full user objects
                runInAction(() => {
                    this.selectedUsers = holidayUsers.map(hu => {
                        const userInfo = users.find(u => u.id === hu.user);
                        return userInfo ? { ...userInfo, holidayUserId: hu.id } : null;
                    }).filter(Boolean);
                });
            }
        } catch (err) {
            console.error('Failed to load holiday users:', err);
        }
    }

    /**
     * Add a user to the holiday planner (saves to API)
     * @param {object} user
     */
    async addSelectedUser(user) {
        if (this.selectedUsers.find(u => u.id === user.id)) {
            return; // Already exists
        }

        try {
            const response = await util.fetchApi('/api/holidayusers/', { method: 'POST' }, {
                user: user.id,
            });
            if (response && (response.status === 200 || response.status === 201)) {
                const holidayUser = response.data?.holidayUser;
                runInAction(() => {
                    this.selectedUsers = [...this.selectedUsers, { ...user, holidayUserId: holidayUser?.id }];
                });
            }
        } catch (err) {
            console.error('Failed to add holiday user:', err);
        }
    }

    /**
     * Remove a user from the holiday planner (deletes from API)
     * @param {number} userId
     */
    async removeSelectedUser(userId) {
        const user = this.selectedUsers.find(u => u.id === userId);
        if (!user || !user.holidayUserId) {
            // Fallback: just remove locally if no holidayUserId
            runInAction(() => {
                this.selectedUsers = this.selectedUsers.filter(u => u.id !== userId);
            });
            return;
        }

        try {
            const response = await util.fetchApi(`/api/holidayusers/${user.holidayUserId}`, { method: 'DELETE' }, {});
            if (response && response.status === 200) {
                runInAction(() => {
                    this.selectedUsers = this.selectedUsers.filter(u => u.id !== userId);
                });
            }
        } catch (err) {
            console.error('Failed to remove holiday user:', err);
        }
    }

    /**
     * Set the selected users list (local only, for compatibility)
     * @param {object[]} users
     */
    @action
    setSelectedUsers(users) {
        this.selectedUsers = users;
    }

    /**
     * Clear the selected users list
     */
    @action
    clearSelectedUsers() {
        this.selectedUsers = [];
    }
}

const store = new HolidayDayStore();
export default store;
